// src/pages/Subscription.js
import Lottie from "lottie-react";
import loadingAnimation from "../tools/Animations/oiiko loading.json";
import { trackEvent } from "../firebase/analytics";
import {
  getCheckoutUrl,
  updateSubscriptionPrice,
  scheduleSubscriptionDowngrade,
  previewSubscriptionChange,
} from "../firebase/stripeFunctions";
import { isPilotActive as computePilotActive } from "../utils/pilot";

import { useState, useEffect, useMemo } from "react";
import { useAuth } from "../Context/AuthContext";
import { db, auth } from "../firebase/config";
import { signOut } from "firebase/auth";
import { ToastContainer, toast } from "react-toastify";

import {
  doc,
  getDoc,
  collection,
  getDocs,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";
import "../styles/Subscription.css";
import { useSubscription } from "../hooks/useSubscription";
import { useNavigate } from "react-router-dom";
import { INDUSTRY_PLAN_NOTES } from "../data/industryPlanNotes";

import StatusBanner from "../components/StatusBanner";
import { formatError } from "../utils/errorMessages";

const TEAM_SIZES = ["1-3", "4-10", "11-20", "21-35", "36-50"];

const PLAN_DESCRIPTIONS = {
  Basic: "Core scheduling and time tools for small teams getting started.",
  Standard: "Everything in Basic plus leave and expenses for growing teams.",
  Premium:
    "Full suite with payroll-ready reports, training, analytics, and IT helpdesk for ops-heavy orgs.",
  Training:
    "Standalone training manager to assign, track, and report on courses.",
  Helpdesk:
    "Standalone IT helpdesk for fast ticket intake and resolution tracking.",
};
const PLAN_BEST_FOR = {
  Basic: "Best for getting started",
  Standard: "Best for growing teams",
  Premium: "Best for operations-heavy teams",
  Training: "Best for onboarding & compliance",
  Helpdesk: "Best for ticketing & IT workflows",
};

const getIndustryNote = (planName, industry) => {
  const map = INDUSTRY_PLAN_NOTES[planName] || {};
  return (industry && map[industry]) || map.default || "";
};

const PLAN_FEATURES = {
  Basic: [
    "Quick hire & self-onboarding",
    "Time Tracking",
    "Shift Scheduling",
    "Payroll-ready reports",
    "Multi location support",
  ],
  Standard: [
    "Quick hire & self-onboarding",
    "Time Tracking",
    "Shift Scheduling",
    "Payroll-ready reports",
    "Leave management",
    "Expense management",
    "Task manager", // ✅ ADD THIS
    "Multi location support",
  ],
  Premium: [
    "Quick hire & self-onboarding",
    "Time Tracking",
    "Shift Scheduling",
    "Payroll-ready reports",
    "Leave management",
    "Expense management",

    "Task manager",
    "Training manager",
    "Reports & Analytics",
    "IT Helpdesk Ticketing",
    "Multi location support",
  ],
  Training: [
    "Assign courses (PDFs, videos, links) to employees",
    "Due dates, reminders, and progress tracking",
    "Upload and organize training resources",
    "Training analytics",
    "Completion reports",
  ],
  Helpdesk: [
    "Employees submit IT tickets from web/mobile",
    "Prioritize and assign tickets to IT staff",
    "Statuses, SLAs, and internal notes",
    "Attachments and activity log",
    "Helpdesk analytics and exports",
  ],
};

const FEATURE_MODULES = {
  "Quick hire & self-onboarding": "Employee Management",
  "Time Tracking": "Time Tracking",
  "Shift Scheduling": "Scheduling",

  "Payroll-ready reports": "Payroll & Reporting",

  "Leave management": "Leave Management",
  "Expense management": "Expenses",

  "Task manager": "Task Manager",
  "Training manager": "Training",
  "Reports & Analytics": "Reports & Analytics",
  "IT Helpdesk Ticketing": "IT Helpdesk",
};

const getModuleLossSummary = (lostFeatures) => {
  const modules = [...new Set(lostFeatures.map((f) => FEATURE_MODULES[f]))];
  return modules.join(", ");
};

const DISPLAY_NAME_MAP = {
  Training: "Training Manager",
  Helpdesk: "IT Helpdesk",
};

const PLAN_ORDER = ["Training", "Helpdesk", "Basic", "Standard", "Premium"];

const getPlanRank = (name) => {
  const idx = PLAN_ORDER.indexOf(name);
  return idx === -1 ? 0 : idx;
};

const isPlanDowngrade = (currentPlan, targetPlan) => {
  const currentRank = getPlanRank(currentPlan);
  const targetRank = getPlanRank(targetPlan);
  return targetRank < currentRank;
};

const getLostFeatures = (currentPlan, targetPlan) => {
  const current = PLAN_FEATURES[currentPlan] || [];
  const target = PLAN_FEATURES[targetPlan] || [];
  return current.filter((feat) => !target.includes(feat));
};

const formatUSDate = (date) => {
  if (!date) return "unknown date";
  return new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(date);
};

const formatEffectiveDate = (value) => {
  if (!value) return "unknown date";

  let d = null;

  // Firestore Timestamp
  if (typeof value?.toDate === "function") {
    d = value.toDate();
  }
  // Stripe unix seconds (number)
  else if (typeof value === "number") {
    d = new Date(value * 1000);
  }
  // { seconds: number }
  else if (typeof value === "object" && typeof value.seconds === "number") {
    d = new Date(value.seconds * 1000);
  }
  // Date string / ms
  else {
    d = new Date(value);
  }

  return formatUSDate(d);
};

export default function Subscription() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [products, setProducts] = useState([]);
  const [customerId, setCustomerId] = useState(null);
  const [billingCycle, setBillingCycle] = useState("monthly");
  const [teamSize, setTeamSize] = useState(null);
  const [industry, setIndustry] = useState(null);
  const [companySub, setCompanySub] = useState(null);

  const { subscription, loading: subLoading } = useSubscription();

  const subscriptionStatus = String(subscription?.status || "").toLowerCase();

  const isFreePlan = subscriptionStatus === "free";

  const hasPaidSubscription = ["active", "trialing"].includes(
    subscriptionStatus,
  );

  const [selectedTeamSize, setSelectedTeamSize] = useState(null);
  const [companyPending, setCompanyPending] = useState(null);
  const [pilot, setPilot] = useState(null);

  const { user } = useAuth();

  const [downgradeDialog, setDowngradeDialog] = useState({
    open: false,
    price: null,
    lostFeatures: [],
  });

  const [upgradeDialog, setUpgradeDialog] = useState({
    open: false,
    price: null,
    preview: null,
  });
  const [upgradeError, setUpgradeError] = useState(null);
  const [companyLoading, setCompanyLoading] = useState(true);
  const [productsLoading, setProductsLoading] = useState(true);
  const [customerLoading, setCustomerLoading] = useState(true);

  useEffect(() => {
    if (teamSize && !selectedTeamSize) {
      setSelectedTeamSize(teamSize);
    }
  }, [teamSize, selectedTeamSize]);
  const effectiveTeamSize = selectedTeamSize || teamSize;

  const showingFreeTier = effectiveTeamSize === "1-3";

  useEffect(() => {
    if (localStorage.getItem("needsSubscriptionRefresh") === "true") {
      localStorage.removeItem("needsSubscriptionRefresh");
      window.location.reload();
    }
  }, []);

  useEffect(() => {
    trackEvent("subscription_view", {
      page: "subscription",
    });
  }, []);

  useEffect(() => {
    if (!user) {
      setCompanyLoading(false);
      return;
    }

    const fetchTeamSize = async () => {
      setCompanyLoading(true);
      try {
        const companyRef = doc(db, "companies", user.uid);
        const companySnap = await getDoc(companyRef);

        if (companySnap.exists()) {
          const companyData = companySnap.data();

          setCompanySub(companyData.subscription || null);
          setTeamSize(companyData.size);
          setIndustry(companyData.industry);
          setCompanyPending(companyData.pendingDowngrade || null);

          const pilotData = companyData.pilot || {
            active: companyData.pilotActive,
            endsAt: companyData.pilotEndsAt,
            startedAt: companyData.pilotStartedAt,
            days: companyData.pilotDays,
          };

          setPilot(pilotData || null);
        } else {
          console.warn("❌ No company found. Redirecting to setup...");
          navigate("/company-setup");
        }
      } catch (err) {
        console.error("Error fetching company data:", err);
        navigate("/company-setup");
      } finally {
        setCompanyLoading(false);
      }
    };

    fetchTeamSize();
  }, [user, navigate]);

  useEffect(() => {
    const fetchProducts = async () => {
      setProductsLoading(true);
      try {
        const productsRef = collection(db, "products");
        const productSnapshot = await getDocs(productsRef);
        const productList = [];

        for (const productDoc of productSnapshot.docs) {
          const productData = productDoc.data();
          const pricesRef = collection(db, `products/${productDoc.id}/prices`);
          const pricesSnapshot = await getDocs(pricesRef);

          const prices = pricesSnapshot.docs
            .map((d) => ({ id: d.id, ...d.data() }))
            .filter((price) => price.active);

          productList.push({
            id: productDoc.id,
            ...productData,
            prices,
          });
        }

        setProducts(productList);
      } catch (err) {
        console.error(err);
        const { title, message } = formatError("subscription", err);
        setError({ title, message });
      } finally {
        setProductsLoading(false);
      }
    };

    fetchProducts();
  }, []);

  useEffect(() => {
    if (!user) {
      setCustomerLoading(false);
      return;
    }

    const fetchCustomerId = async () => {
      setCustomerLoading(true);
      try {
        const customerRef = doc(db, "customers", user.uid);
        const customerSnap = await getDoc(customerRef);

        if (customerSnap.exists()) {
          const customerData = customerSnap.data();
          setCustomerId(customerData.stripeId);
        } else {
          setError({
            title: "Billing Profile Missing",
            message:
              "We couldn’t find a Stripe billing profile for this account. Please contact Oiiko Support so we can finish your setup.",
          });
        }
      } catch (err) {
        console.error(err);
        const { title, message } = formatError("subscription", err);
        setError({ title, message });
      } finally {
        setCustomerLoading(false);
      }
    };

    fetchCustomerId();
  }, [user]);

  const alreadySubscribed = hasPaidSubscription;

  const isPilotActive = computePilotActive(pilot);
  const shouldBlockPlanSelection = isPilotActive && !alreadySubscribed;

  const pilotEnded = !!pilot?.endsAt && !isPilotActive && !alreadySubscribed;
  const showPilotEndedBanner = pilotEnded;

  const pilotEndsLabel = pilot?.endsAt
    ? formatEffectiveDate(pilot.endsAt)
    : null;

  const pendingDowngrade =
    subscription?.pendingDowngrade || companyPending || null;

  const toSec = (val) => {
    if (!val) return null;
    if (typeof val?.toDate === "function")
      return Math.floor(val.toDate().getTime() / 1000);
    if (typeof val === "number")
      return val > 1e12 ? Math.floor(val / 1000) : val;
    if (typeof val === "object" && typeof val.seconds === "number")
      return val.seconds;
    return null;
  };

  const stripeCancelAtPeriodEnd = !!subscription?.cancel_at_period_end;
  const stripeCancelAtSec = toSec(subscription?.cancel_at);

  const companyIsCancelling =
    (companySub?.subscriptionStatus || companySub?.status || "")
      .toString()
      .toLowerCase() === "cancelling";

  const companyCancelAtPeriodEnd = !!companySub?.cancelAtPeriodEnd;
  const companyCancelAtSec = toSec(companySub?.cancelAt);

  const cancelAtPeriodEnd =
    stripeCancelAtPeriodEnd || companyCancelAtPeriodEnd || companyIsCancelling;

  const periodEndSec =
    toSec(subscription?.current_period_end) ||
    toSec(companySub?.currentPeriodEnd);

  const effectiveCancelSec =
    stripeCancelAtSec || companyCancelAtSec || periodEndSec || null;
  const cancelsOnLabel = effectiveCancelSec
    ? formatEffectiveDate(effectiveCancelSec) // you already have formatEffectiveDate()
    : null;

  const bannerLostFeatures =
    pendingDowngrade && subscription?.planName
      ? getLostFeatures(
          subscription.planName,
          pendingDowngrade.targetProductName,
        )
      : [];

  const canAccessDashboard = isFreePlan || hasPaidSubscription;

  const handleSelectFreePlan = async () => {
    if (!user?.uid) return;

    setLoading(true);
    setError(null);

    try {
      const userRef = doc(db, "users", user.uid);
      const userSnap = await getDoc(userRef);

      if (!userSnap.exists()) {
        throw new Error("User profile not found.");
      }

      const companyId = userSnap.data()?.companyId;

      if (!companyId) {
        throw new Error("Company ID not found.");
      }

      const companyRef = doc(db, "companies", companyId);

      await updateDoc(companyRef, {
        subscription: {
          status: "free",
          planName: "Free",
          productId: null,
          priceId: null,
          billingInterval: null,
          teamSize: "1-3",
          employeeLimit: 3,
          currentPeriodEnd: null,
          cancelAtPeriodEnd: false,
          cancelAt: null,
          trialStart: null,
          trialEnd: null,
          updatedAt: serverTimestamp(),
        },

        plan: "Free",
        teamSize: "1-3",
        employeeLimit: 3,
        subscriptionStatus: "free",
      });

      await updateDoc(userRef, {
        "onboarding.step": "dashboard",
        "onboarding.emailVerified": true,
        "onboarding.companyCreated": true,
        "onboarding.subscriptionStatus": "free",
        "onboarding.lastUpdatedAt": serverTimestamp(),
      });

      trackEvent("free_plan_selected", {
        team_size: "1-3",
        plan_name: "Free",
      });

      navigate("/admin-dashboard", {
        replace: true,
      });
    } catch (err) {
      console.error("Failed to select Free plan:", err);

      setError({
        title: "Could Not Activate Free Plan",
        message:
          err?.message ||
          "We could not activate your Free plan. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleBackToDashboard = () => {
    if (!canAccessDashboard) return;
    navigate("/admin-dashboard");
  };

  const handlePilotBackToDashboard = () => {
    navigate("/admin-dashboard");
  };

  const handleBackToLogin = async () => {
    try {
      setLoading(true);
      await signOut(auth);
    } catch (err) {
      console.error("Error signing out:", err);
    } finally {
      setLoading(false);
      navigate("/login");
    }
  };

  const handleSubscribe = async (priceId, price) => {
    trackEvent("subscription_plan_selected", {
      plan_name: price?.planName || "unknown",
      billing_cycle: billingCycle,
      team_size: selectedTeamSize || teamSize || "unknown",
      price_id: priceId,
      has_active_subscription: hasPaidSubscription,
      is_free_plan: isFreePlan,
      pilot_blocked: shouldBlockPlanSelection,
    });
    if (shouldBlockPlanSelection) {
      toast.info(
        pilotEndsLabel
          ? `You're currently in a Pilot Program. You can subscribe after it ends on ${pilotEndsLabel}.`
          : "You're currently in a Pilot Program. You can subscribe once your pilot ends.",
      );
      return;
    }

    setError(null);

    try {
      const companyRef = doc(db, "companies", user.uid);
      const companySnap = await getDoc(companyRef);
      const companyData = companySnap.data();

      if (companyData?.pendingDowngrade) {
        toast.error(
          "You already have a scheduled downgrade. Cancel it before changing plans.",
        );
        return;
      }

      if (!hasPaidSubscription) {
        setLoading(true);

        if (!customerId) {
          // eslint-disable-next-line no-throw-literal
          throw {
            code: "subscription/no-stripe-customer",
            message: "No Stripe customer ID found.",
          };
        }

        const checkoutUrl = await getCheckoutUrl(db, user.uid, priceId);
        trackEvent("checkout_started", {
          plan_name: price?.planName || "unknown",
          billing_cycle: billingCycle,
          team_size: selectedTeamSize || teamSize || "unknown",

          price_id: priceId,
        });
        window.location.assign(checkoutUrl);

        return;
      }

      const currentPlan = subscription.planName;
      const targetPlan = price.planName;

      const downgrade = isPlanDowngrade(currentPlan, targetPlan);

      if (!downgrade) {
        setLoading(true);

        const preview = await previewSubscriptionChange(priceId);
        setUpgradeError(null);
        trackEvent("upgrade_preview_opened", {
          current_plan: currentPlan,
          target_plan: targetPlan,
          billing_cycle: billingCycle,
          team_size: selectedTeamSize || teamSize || "unknown",
        });
        setUpgradeDialog({
          open: true,
          price: { ...price, id: priceId },
          preview,
        });

        setLoading(false);
        return;
      }

      const lostFeatures = getLostFeatures(currentPlan, targetPlan);
      trackEvent("downgrade_modal_opened", {
        current_plan: currentPlan,
        target_plan: targetPlan,
        lost_features_count: lostFeatures.length,
      });
      setDowngradeDialog({
        open: true,
        price: { ...price, id: priceId },
        lostFeatures,
      });
    } catch (err) {
      console.error(err);
      trackEvent("subscription_error", {
        action: "handle_subscribe",
        error_code: err.code || err.message || "unknown",
      });
      const { title, message } = formatError("subscription", err);
      setError({ title, message });
    }
  };

  const handleConfirmDowngrade = async () => {
    if (!downgradeDialog.open || !downgradeDialog.price) return;

    const { price } = downgradeDialog;
    const targetPlan = price.planName;
    trackEvent("downgrade_confirmed", {
      target_plan: targetPlan,
    });
    setLoading(true);
    setError(null);

    try {
      const result = await scheduleSubscriptionDowngrade(price.id);
      console.log("✅ Downgrade scheduled:", result);
      trackEvent("downgrade_scheduled_success", {
        target_plan: targetPlan,
        effective_at: result?.effectiveAt || "unknown",
      });
      const effectiveAt = result?.effectiveAt;
      let msg = `Your downgrade to ${targetPlan} has been scheduled and will take effect at the end of your current billing period.`;

      if (effectiveAt) {
        const dateStr = new Date(effectiveAt * 1000).toLocaleDateString();
        msg = `Your downgrade to ${targetPlan} is scheduled and will take effect on ${dateStr}.`;
      }

      if (downgradeDialog.lostFeatures.length) {
        msg += ` After that date, the following features will be disabled: ${downgradeDialog.lostFeatures.join(
          ", ",
        )}. Your existing data will remain stored and will be available again if you upgrade back to a higher plan in the future.`;
      }

      toast.success(msg, { autoClose: 6000 });
      setDowngradeDialog({ open: false, price: null, lostFeatures: [] });
    } catch (err) {
      console.error(err);
      trackEvent("subscription_error", {
        action: "confirm_downgrade",
        error_code: err.code || err.message || "unknown",
      });
      const { title, message } = formatError("subscription", err);
      setError({ title, message });
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmUpgrade = async () => {
    if (!upgradeDialog.open || !upgradeDialog.price) return;
    trackEvent("upgrade_confirmed", {
      target_plan: upgradeDialog.price.planName || "unknown",
    });
    setLoading(true);
    setError(null);
    setUpgradeError(null);

    try {
      const result = await updateSubscriptionPrice(
        upgradeDialog.price.id,
        upgradeDialog.preview.prorationDate,
      );
      console.log("✅ Plan changed (upgrade):", result);
      trackEvent("upgrade_success", {
        target_plan: upgradeDialog.price.planName || "unknown",
        pending_update: !!result.pendingUpdate,
      });
      if (result.pendingUpdate) {
        trackEvent("upgrade_payment_incomplete", {
          target_plan: upgradeDialog.price.planName || "unknown",
        });
        setUpgradeError(
          "Payment was not completed. Please verify your payment method and try again.",
        );
        return;
      }

      toast.success("Your plan was upgraded successfully.", {
        autoClose: 5000,
      });

      setUpgradeDialog({
        open: false,
        price: null,
        preview: null,
      });

      navigate("/admin-dashboard");
    } catch (err) {
      console.error(err);
      trackEvent("subscription_error", {
        action: "confirm_upgrade",
        error_code: err.code || err.message || "unknown",
      });
      const { title, message } = formatError("subscription", err);
      setError({ title, message });
    } finally {
      setLoading(false);
    }
  };

  const handleCancelUpgrade = () => {
    setUpgradeError(null);
    setUpgradeDialog({
      open: false,
      price: null,
      preview: null,
    });
  };

  const handleCancelScheduledDowngrade = async () => {
    try {
      const current = auth.currentUser;
      if (!current)
        throw new Error("You must be logged in to cancel a downgrade.");

      const idToken = await current.getIdToken();

      const res = await fetch(
        `${process.env.REACT_APP_FUNCTIONS_URL}/cancelScheduledDowngrade`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${idToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({}),
        },
      );

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to cancel downgrade.");

      toast.success("Your scheduled downgrade has been canceled.", {
        autoClose: 5000,
      });

      setCompanyPending(null);
    } catch (err) {
      console.error(err);
      toast.error(err.message || "Failed to cancel downgrade.");
    }
  };

  const handleCancelDowngrade = () => {
    setDowngradeDialog({ open: false, price: null, lostFeatures: [] });
  };

  const planPriceMap = useMemo(() => {
    const map = {};
    if (!teamSize || products.length === 0) return map;

    products.forEach((product) => {
      const planName = product.metadata?.name || product.name;
      product.prices.forEach((price) => {
        const priceTeamSize = price.metadata?.teamSize;
        const interval = price.recurring?.interval;
        const effTeamSize = selectedTeamSize || teamSize;
        if (priceTeamSize !== effTeamSize) return;

        if (!map[planName]) map[planName] = {};
        if (interval === "month") map[planName].monthlyPrice = price;
        if (interval === "year") map[planName].yearlyPrice = price;
      });
    });

    return map;
  }, [products, teamSize, selectedTeamSize]);

  const recommendedPrices = useMemo(() => {
    if (!teamSize) return [];
    let out = [];

    products.forEach((product) => {
      const planName = product.metadata?.name || product.name;

      product.prices.forEach((price) => {
        const priceTeamSize = price.metadata?.teamSize;
        const interval = price.recurring?.interval;
        const matchesInterval =
          interval === (billingCycle === "yearly" ? "year" : "month");

        const effTeamSize = selectedTeamSize || teamSize;

        if (priceTeamSize === effTeamSize && matchesInterval) {
          let savingsCents = 0;
          let savingsPct = 0;

          if (interval === "year") {
            const monthly = planPriceMap[planName]?.monthlyPrice;
            if (monthly?.unit_amount) {
              const monthlyYearTotal = monthly.unit_amount * 12;
              savingsCents = Math.max(0, monthlyYearTotal - price.unit_amount);
              if (monthlyYearTotal > 0) {
                savingsPct = Math.round(
                  (savingsCents / monthlyYearTotal) * 100,
                );
              }
            }
          }

          out.push({
            ...price,
            planName,
            displayName: DISPLAY_NAME_MAP[planName] || planName,
            features: PLAN_FEATURES[planName],
            savingsCents,
            savingsPct,
          });
        }
      });
    });

    const preferredOrder = [
      "Basic",
      "Standard",
      "Premium",
      "Training",
      "Helpdesk",
    ];
    out.sort(
      (a, b) =>
        preferredOrder.indexOf(a.planName) - preferredOrder.indexOf(b.planName),
    );

    return out;
  }, [products, teamSize, billingCycle, planPriceMap, selectedTeamSize]);

  const formatCurrency = (cents, currency = "usd") =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency.toUpperCase(),
    }).format((cents || 0) / 100);

  const formatMoney = (cents) => `$${(cents / 100).toFixed(2)}`;
  const isMostPopular = (planName) => planName === "Premium";

  const isPageLoading =
    subLoading || companyLoading || productsLoading || customerLoading;

  if (isPageLoading) {
    return (
      <div className="admin-loading-screen">
        <Lottie
          animationData={loadingAnimation}
          loop
          autoplay
          className="admin-loading-lottie"
        />
        <p className="admin-loading-text">Loading your subscription…</p>
      </div>
    );
  }

  return (
    <div className="subscription-container">
      {/* ✅ TOP BANNERS (FULL WIDTH, ABOVE BUTTON) */}
      <div className="top-banners">
        {showPilotEndedBanner && (
          <div
            className="pilot-ended-banner banner-bar"
            role="status"
            aria-live="polite"
          >
            <strong>Pilot Program ended.</strong>{" "}
            {pilotEndsLabel ? (
              <>
                Your pilot access ended on <strong>{pilotEndsLabel}</strong>.
                Please choose a plan below to continue using Oiiko.
              </>
            ) : (
              <>
                Your pilot access has ended. Please choose a plan below to
                continue using Oiiko.
              </>
            )}
            <div className="pilot-ended-banner-sub">
              You will regain access immediately after subscribing.
            </div>
          </div>
        )}

        {shouldBlockPlanSelection && (
          <div
            className="pilot-banner banner-bar"
            role="status"
            aria-live="polite"
          >
            <strong>Pilot Program active.</strong>{" "}
            {pilotEndsLabel ? (
              <>
                You can subscribe after your pilot ends on{" "}
                <strong>{pilotEndsLabel}</strong>.
              </>
            ) : (
              <>You can subscribe after your pilot ends.</>
            )}
            <div className="pilot-banner-sub">
              You can still view all plans now, but checkout is disabled during
              the pilot.
            </div>
          </div>
        )}

        {cancelAtPeriodEnd && (
          <div
            className="cancel-scheduled-banner banner-bar"
            role="status"
            aria-live="polite"
          >
            <strong>Cancellation scheduled.</strong>{" "}
            {cancelsOnLabel ? (
              <>
                You’ll keep access until <strong>{cancelsOnLabel}</strong>.
              </>
            ) : (
              <>You’ll keep access until the end of your current period.</>
            )}
          </div>
        )}

        {pendingDowngrade && (
          <div className="pending-downgrade-banner banner-bar">
            <div className="pending-downgrade-left">
              <div className="pending-downgrade-icon" aria-hidden="true">
                ⏳
              </div>
              <div className="pending-downgrade-copy">
                <div className="pending-downgrade-label">
                  Downgrade scheduled
                </div>

                <p className="pending-downgrade-text">
                  Your plan will move to{" "}
                  <strong>{pendingDowngrade.targetProductName}</strong> on{" "}
                  <strong>
                    {formatEffectiveDate(pendingDowngrade.effectiveAt)}
                  </strong>
                  . You’ll keep full access to your current plan until that
                  date.
                </p>

                {bannerLostFeatures.length > 0 && (
                  <p className="pending-downgrade-text pending-downgrade-subtext">
                    When this downgrade takes effect, you’ll lose{" "}
                    <strong>{bannerLostFeatures.join(", ")}</strong>. Your data
                    in these modules will be kept and will become visible again
                    if you upgrade back to a higher plan.
                  </p>
                )}
              </div>
            </div>

            <div className="pending-downgrade-right">
              <button
                type="button"
                className="pending-downgrade-cancel"
                onClick={handleCancelScheduledDowngrade}
                disabled={loading}
              >
                Cancel downgrade
              </button>
            </div>
          </div>
        )}

        {/* Global subscription error banner (still at top) */}
        {error && (
          <div className="banner-bar status-banner-wrap">
            <StatusBanner
              type="error"
              title={error.title}
              message={error.message}
              onClose={() => setError(null)}
            />
          </div>
        )}
      </div>

      {/* ✅ Back button row UNDER banners */}
      <div className="sub-header under-banners">
        <div className="sub-header1">
          {shouldBlockPlanSelection ? (
            <button
              type="button"
              className="back-link"
              onClick={handlePilotBackToDashboard}
              disabled={loading}
              aria-label="Back to dashboard"
            >
              ← Back to dashboard
            </button>
          ) : canAccessDashboard ? (
            <button
              type="button"
              className="back-link"
              onClick={handleBackToDashboard}
              disabled={loading}
              aria-label="Back to dashboard"
            >
              ← Back to dashboard
            </button>
          ) : (
            <button
              type="button"
              className="back-link"
              onClick={handleBackToLogin}
              disabled={loading}
              aria-label="Back to login"
            >
              ← Back to login
            </button>
          )}
        </div>

        {/* (Optional right area kept for spacing symmetry) */}
        <div className="sub-header2" />
      </div>

      <h2 style={{ marginTop: "5px" }}>Choose a plan for your team</h2>

      {industry && (
        <p className="industry-note">
          Many {industry} companies prefer our Premium plan!
        </p>
      )}

      {/* Team size pill group */}
      <div className="team-size">
        <div
          className="size-pills"
          role="radiogroup"
          aria-label="Team size range"
          onKeyDown={(e) => {
            const sizes = TEAM_SIZES;
            const current = selectedTeamSize || teamSize || sizes[0];
            const idx = sizes.indexOf(current);
            if (e.key === "ArrowRight") {
              const next = sizes[Math.min(idx + 1, sizes.length - 1)];
              setSelectedTeamSize(next);
            }
            if (e.key === "ArrowLeft") {
              const prev = sizes[Math.max(idx - 1, 0)];
              setSelectedTeamSize(prev);
            }
          }}
          tabIndex={0}
        >
          {TEAM_SIZES.map((s) => {
            const active = (selectedTeamSize || teamSize) === s;
            return (
              <button
                key={s}
                type="button"
                role="radio"
                aria-checked={active}
                className={`size-pill ${active ? "active" : ""}`}
                onClick={() => {
                  trackEvent("subscription_team_size_selected", {
                    team_size: s,
                  });
                  setSelectedTeamSize(s);
                }}
                disabled={loading}
              >
                {s}
              </button>
            );
          })}
        </div>
      </div>

      <div className="billing-toggle" role="tablist" aria-label="Billing cycle">
        <button
          role="tab"
          aria-selected={billingCycle === "monthly"}
          className={billingCycle === "monthly" ? "active" : ""}
          onClick={() => {
            trackEvent("billing_cycle_selected", {
              billing_cycle: "monthly",
            });
            setBillingCycle("monthly");
          }}
        >
          Monthly
        </button>
        <button
          role="tab"
          aria-selected={billingCycle === "yearly"}
          className={billingCycle === "yearly" ? "active" : ""}
          onClick={() => {
            trackEvent("billing_cycle_selected", {
              billing_cycle: "yearly",
            });
            setBillingCycle("yearly");
          }}
        >
          Yearly
        </button>
      </div>

      {/* Downgrade confirmation modal */}
      {downgradeDialog.open && downgradeDialog.price && (
        <div className="downgrade-backdrop" role="presentation">
          <div className="downgrade-modal" role="dialog" aria-modal="true">
            <div className="downgrade-header">
              <div>
                <h3 className="downgrade-title">Confirm plan downgrade</h3>
                <p className="downgrade-subtitle">
                  You’re switching to{" "}
                  <strong>{downgradeDialog.price.planName}</strong>.
                </p>
              </div>

              <button
                type="button"
                className="downgrade-close"
                onClick={handleCancelDowngrade}
                disabled={loading}
                aria-label="Close"
              >
                ✕
              </button>
            </div>

            <div className="downgrade-summary">
              <div className="downgrade-summary-row">
                <span>Change takes effect</span>
                <strong>End of current billing period</strong>
              </div>
              <div className="downgrade-summary-row">
                <span>Until then</span>
                <strong>You keep full access</strong>
              </div>
            </div>

            {downgradeDialog.lostFeatures.length > 0 && (
              <div className="downgrade-loss">
                <div className="downgrade-loss-top">
                  <span className="downgrade-loss-badge">
                    You’ll lose access to
                  </span>
                  <span className="downgrade-loss-modules">
                    {getModuleLossSummary(downgradeDialog.lostFeatures)}
                  </span>
                </div>

                <p className="downgrade-loss-note">
                  Your existing data in these modules will be kept. If you
                  upgrade again later, everything becomes visible and accessible
                  again.
                </p>
              </div>
            )}

            <div className="downgrade-actions">
              <button
                type="button"
                className="downgrade-btn downgrade-btn-ghost"
                onClick={handleCancelDowngrade}
                disabled={loading}
              >
                Keep current plan
              </button>

              <button
                type="button"
                className="downgrade-btn downgrade-btn-danger"
                onClick={handleConfirmDowngrade}
                disabled={loading}
              >
                {loading ? "Scheduling..." : "Confirm downgrade"}
              </button>
            </div>

            <p className="downgrade-footnote">
              Tip: You can cancel a scheduled downgrade anytime before it takes
              effect.
            </p>
          </div>
        </div>
      )}
      {upgradeDialog.open && upgradeDialog.price && upgradeDialog.preview && (
        <div className="downgrade-backdrop" role="presentation">
          <div className="downgrade-modal" role="dialog" aria-modal="true">
            <div className="downgrade-header">
              <div>
                <h3 className="downgrade-title">Confirm plan upgrade</h3>
                <p className="downgrade-subtitle">
                  You’re switching to{" "}
                  <strong>{upgradeDialog.price.planName}</strong>.
                </p>
              </div>

              <button
                type="button"
                className="downgrade-close"
                onClick={handleCancelUpgrade}
                disabled={loading}
                aria-label="Close"
              >
                ✕
              </button>
              {upgradeError && (
                <div className="upgrade-inline-error" role="alert">
                  {upgradeError}
                </div>
              )}
            </div>

            <div className="downgrade-summary">
              <div className="downgrade-summary-row">
                <span>Charge today</span>
                <strong>
                  {formatCurrency(
                    upgradeDialog.preview.amountDueNow,
                    upgradeDialog.preview.currency,
                  )}
                </strong>
              </div>

              <div className="downgrade-summary-row">
                <span>New recurring price</span>
                <strong>{formatMoney(upgradeDialog.price.unit_amount)}</strong>
              </div>
            </div>

            <div className="downgrade-loss">
              <div className="downgrade-loss-top">
                <span className="downgrade-loss-badge">Invoice preview</span>
              </div>

              <ul className="feature-list">
                {upgradeDialog.preview.lines.map((line) => (
                  <li key={line.id}>
                    {line.description}:{" "}
                    {formatCurrency(line.amount, line.currency)}
                  </li>
                ))}
              </ul>
            </div>

            <div className="downgrade-actions">
              <button
                type="button"
                className="downgrade-btn downgrade-btn-ghost"
                onClick={handleCancelUpgrade}
                disabled={loading}
              >
                Cancel
              </button>

              <button
                type="button"
                className="downgrade-btn downgrade-btn-danger"
                onClick={handleConfirmUpgrade}
                disabled={loading}
              >
                {loading ? "Processing..." : "Confirm upgrade"}
              </button>
            </div>
          </div>
        </div>
      )}
      <div className="pricing-grid">
        {showingFreeTier && (
          <div className="pricing-card">
            <div className="card-top">
              <span className="ybadge ybadge-current">Free Forever</span>

              <h3 className="plan-title">Basic Plan</h3>

              <div className="price-line">
                <span className="subprice">$0.00</span>
                <span className="price-interval">/forever</span>
              </div>
            </div>

            <div className="divider" />

            <h3 className="plan-title">Free Plan</h3>

            <div className="plan-bestfor">
              Best for small teams getting started
            </div>

            <p className="plan-desc">Manage up to 3 employees for free.</p>

            <ul className="feature-list" aria-label="Free plan features">
              <li className="included">✔ Up to 3 employees</li>
              <li className="included">✔ Quick hire and self-onboarding</li>
              <li className="included">✔ Time tracking</li>
              <li className="included">✔ Shift scheduling</li>
              <li className="included">✔ Payroll-ready reports</li>
              <li className="included">✔ Multi-location support</li>
            </ul>

            <div className="card-cta">
              <button
                className="choose-plan-btn"
                type="button"
                onClick={handleSelectFreePlan}
                disabled={loading || isFreePlan}
              >
                {loading
                  ? "Activating..."
                  : isFreePlan
                  ? "Current Plan"
                  : "Continue with Free"}
              </button>
            </div>
          </div>
        )}
        {recommendedPrices.length === 0 && (
          <p className="empty-state">
            No paid plans are currently available for this team size.
          </p>
        )}

        {recommendedPrices.map((price) => {
          const showSavings =
            billingCycle === "yearly" && price.savingsCents > 0;

          const isSameInterval =
            (billingCycle === "yearly" &&
              price.recurring?.interval === "year") ||
            (billingCycle === "monthly" &&
              price.recurring?.interval === "month");

          const isCurrent =
            alreadySubscribed &&
            isSameInterval &&
            price.planName === subscription.planName &&
            (selectedTeamSize || teamSize) === subscription.teamSize;

          const isPopular = isMostPopular(price.planName) && !isCurrent;

          return (
            <div
              className={`pricing-card ${isPopular ? "popular" : ""}`}
              key={price.id}
            >
              <div className="card-top">
                {isPopular && (
                  <span className="xbadge xbadge-popular">Most popular</span>
                )}
                {isCurrent && (
                  <span className="ybadge ybadge-current">Current Plan</span>
                )}
                <h3 className="plan-title">{price.planName}</h3>

                <div className="price-line">
                  <span className="subprice">
                    {formatMoney(price.unit_amount)}
                  </span>

                  <span className="price-interval">
                    {billingCycle === "yearly" ? "/year" : "/month"}
                  </span>
                </div>

                {billingCycle === "yearly" && (
                  <div className="billed-note">Billed annually</div>
                )}

                {showSavings && (
                  <div className="savings-pill">
                    Save {price.savingsPct}% · {formatMoney(price.savingsCents)}
                    /yr
                  </div>
                )}
              </div>

              <div className="divider" />

              <h3 className="plan-title">{price.displayName}</h3>
              <div className="plan-bestfor">
                {PLAN_BEST_FOR[price.planName]}
              </div>
              <p className="plan-desc">{PLAN_DESCRIPTIONS[price.planName]}</p>

              {industry && (
                <div
                  className="industry-hint"
                  role="note"
                  aria-label="Industry recommendation"
                >
                  <span className="hint-dot" aria-hidden="true" />
                  {getIndustryNote(price.planName, industry)}
                </div>
              )}

              <ul
                className="feature-list"
                aria-label={`${price.displayName} features`}
              >
                {price.features?.map((feature, idx) => (
                  <li key={idx} className="included">
                    ✔ {feature}
                  </li>
                ))}
              </ul>

              <div className="card-cta">
                <button
                  className="choose-plan-btn"
                  onClick={() => handleSubscribe(price.id, price)}
                  disabled={loading || isCurrent || shouldBlockPlanSelection}
                  aria-label={`Choose ${
                    price.displayName
                  } plan for ${formatMoney(price.unit_amount)} ${
                    billingCycle === "yearly" ? "per year" : "per month"
                  }`}
                >
                  {loading
                    ? "Processing..."
                    : shouldBlockPlanSelection
                    ? "Pilot program active"
                    : isCurrent
                    ? "Current Plan"
                    : "Choose Plan"}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      <ToastContainer
        position="top-right"
        autoClose={4000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        pauseOnHover
        draggable
        theme="light"
      />
    </div>
  );
}
